import { IconArrowLeft } from "@tabler/icons-react";
import {
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useNavigate,
} from "@tanstack/react-router";
import { toast } from "sonner";

import type { ClusterFormValues } from "@/k8s-clusters/schemas";

import { useTRPC } from "@/infrastructure/trpc/react";
import { ClusterForm } from "@/k8s-clusters/components/cluster-form";
import { AppLayout } from "@/shared/layout";
import { Button } from "@/shared/ui/button";

export const Route = createFileRoute(
  "/app/organization/$id/clusters/add"
)({
  component: AddClusterPage,
  head: () => ({
    meta: [
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
});

function AddClusterPage() {
  const { id: organizationId } = Route.useParams();
  const navigate = useNavigate();
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const createCluster = useMutation(
    trpc.k8sClusters.create.mutationOptions({
      onSuccess: async () => {
        toast.success("K8s cluster added successfully");
        await queryClient.invalidateQueries({
          queryKey: trpc.k8sClusters.list.queryKey({
            organizationId,
          }),
        });
        void navigate({
          to: "/app/organization/$id/settings/k8s-clusters",
          params: { id: organizationId },
        });
      },
      onError: (error) => {
        toast.error(
          error.message ?? "Failed to add cluster"
        );
      },
    })
  );

  const handleSubmit = async (
    values: ClusterFormValues
  ) => {
    await createCluster.mutateAsync({
      organizationId,
      ...values,
    });
  };

  return (
    <AppLayout
      title="Add K8s Cluster"
      headerActions={
        <Link
          to="/app/organization/$id/settings/k8s-clusters"
          params={{ id: organizationId }}
        >
          <Button variant="outline" size="sm">
            <IconArrowLeft className="mr-2 size-4" />
            Back to Settings
          </Button>
        </Link>
      }
    >
      <div className="mx-auto max-w-5xl">
        <ClusterForm
          organizationId={organizationId}
          mode="add"
          onSubmit={handleSubmit}
          isSubmitting={createCluster.isPending}
          cardDescription="Add a Kubernetes cluster to your organization. The cluster will be available for all projects."
          submitButtonText="Add Cluster"
          submittingButtonText="Adding..."
        />
      </div>
    </AppLayout>
  );
}
